import {
  BLOCK_GROUPS,
  RESTRICTED_BLOCK_TYPES,
  blockSchema,
  type Block,
  type BlockType,
  type Role,
} from '@geoic/shared';

export type BlockMeta = {
  label: string;
  /** «Blok qo'shish» modalidagi bir qatorli izoh. */
  hint: string;
};

/**
 * Blok turlarining o'zbekcha nomlari va izohlari (D4).
 *
 * Izoh MAJBURIY: «Ustunliklar» va «Raqamlar» nomlari o'xshash, xodim
 * qaysi biri kerakligini nomdan tushunmaydi. Modalda har biri ostida
 * bir qator tushuntirish turadi.
 */
export const BLOCK_META: Record<BlockType, BlockMeta> = {
  // Umumiy
  'hero-slider': { label: 'Bosh slayder', hint: 'Katta rasmli slaydlar, sarlavha va tugma' },
  'page-header': { label: 'Sahifa banneri', hint: 'Sahifa boshidagi sarlavha va rasm' },
  'rich-text': { label: 'Matn', hint: 'Oddiy matn — sarlavha, ro‘yxat, havola' },
  'text-image': { label: 'Matn + rasm', hint: 'Chap yoki o‘ng tomonda rasm bilan matn' },
  stats: { label: 'Raqamlar', hint: 'Ko‘rsatkichlar: «120+ loyiha», «15 yil»' },
  features: { label: 'Ustunliklar', hint: 'Belgi, sarlavha va matndan iborat kartochkalar' },
  gallery: { label: 'Galereya', hint: 'Rasmlar to‘plami, bosilganda kattalashadi' },
  video: { label: 'Video', hint: 'YouTube havolasi yoki yuklangan video' },
  faq: { label: 'Savol-javob', hint: 'Ochiladigan savollar ro‘yxati' },
  accordion: { label: 'Akkordeon', hint: 'Ochiladigan bo‘limlar — uzun matnni yig‘ish uchun' },
  timeline: { label: 'Xronologiya', hint: 'Yillar bo‘yicha tarix' },
  partners: { label: 'Hamkorlar', hint: 'Logotiplar qatori' },
  cta: { label: 'Chaqiriq', hint: 'Rangli banner va tugma — «Ariza qoldiring»' },
  map: { label: 'Xarita', hint: 'Yandex xarita va manzil belgisi' },
  html: { label: 'HTML kod', hint: 'Xom kod — faqat bosh administrator uchun' },

  // Ro'yxatlar
  'news-list': { label: 'Yangiliklar', hint: 'Oxirgi yangiliklar avtomatik chiqadi' },
  'services-list': { label: 'Xizmatlar', hint: 'Xizmatlar kartochkalari' },
  'projects-list': { label: 'Loyihalar', hint: 'Bajarilgan loyihalar galereyasi' },
  'staff-list': { label: 'Rahbariyat', hint: 'Rahbarlar ro‘yxati, foto bilan' },
  'documents-list': { label: 'Hujjatlar', hint: 'PDF hujjatlar, yuklab olish tugmasi bilan' },
  'courses-list': { label: 'Kurslar', hint: 'O‘quv kurslari va guruhlar jadvali' },
  'services-price': { label: 'Xizmat narxlari', hint: 'Narxlar jadvali — ta’mirlash uchun' },

  // Do'kon
  'products-grid': { label: 'Mahsulotlar to‘plami', hint: 'Katalogdan tanlangan mahsulotlar' },
  'product-slider': {
    label: 'Mahsulotlar karuseli',
    hint: 'Surib ko‘riladigan mahsulotlar qatori',
  },
  'categories-grid': { label: 'Kategoriyalar', hint: 'Katalog bo‘limlari kartochkalari' },
  'brands-strip': { label: 'Brendlar', hint: 'Brend logotiplari qatori' },
  'product-compare': { label: 'Taqqoslash', hint: 'Ikki-to‘rt mahsulotni jadvalda solishtirish' },

  // Boshqa
  'droneshow-packages': { label: 'Dron-shou paketlari', hint: 'Paketlar va narxlari' },
  'droneshow-portfolio': { label: 'Dron-shou portfolio', hint: 'O‘tkazilgan shoular galereyasi' },
  'request-form': { label: 'Ariza formasi', hint: 'Murojaat formasi — 7 turdan biri' },
  'contact-info': { label: 'Kontaktlar', hint: 'Telefon, pochta, manzil — sozlamalardan olinadi' },
};

export type BlockGroupName = keyof typeof BLOCK_GROUPS;

export const BLOCK_GROUP_ORDER = Object.keys(BLOCK_GROUPS) as BlockGroupName[];

/**
 * Yangi blok yasaydi.
 *
 * Standart qiymatlar QO'LDA yozilmaydi — `blockSchema` ning o'zidan
 * olinadi. Qo'lda yozilsa, sxemadagi standart o'zgarganda admin eski
 * qiymat bilan blok yaratib, server uni rad etardi.
 */
export function createBlock(type: BlockType, order: number): Block {
  return blockSchema.parse({ _id: newBlockId(), type, order, data: {} });
}

/**
 * Blok identifikatori.
 *
 * `crypto.randomUUID` emas: `_id` maksimal 40 belgi va u hujjat ICHIDA
 * noyob bo'lishi yetarli — global noyoblik shart emas.
 */
export function newBlockId(): string {
  return `blk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** `html` blokini faqat bosh administrator qo'sha oladi (TZ 4.2). */
export function canUseBlock(type: BlockType, role: Role): boolean {
  return !RESTRICTED_BLOCK_TYPES.includes(type) || role === 'superadmin';
}
